<template>
    <div
        class="group"
        :class="[backgroundClass, textColorClass, borderVerticalClasses]"
        @mouseover="handleRowMouseOver"
        @mousedown="handleRowMouseDown"
        @mouseup="setInactive"
        @mouseleave="setInactive"
    >
        <div
            class="flex flex-row items-center pl-2 w-full font-light gap-2 shrink-0"
            :class="[borderRightClasses]"
            :style="minHeightStyle"
        >
            <Tooltip v-if="checkbox.status !== 'No-Selection'">
                <template #trigger="tooltipProps">
                    <input
                        ref="checkboxRef"
                        :aria-labelledby="labelId"
                        :aria-describedby="tooltipProps['aria-labelledby']"
                        :popovertarget="tooltipProps['popovertarget']"
                        :checked="isChecked"
                        :aria-disabled="isDisabled"
                        type="checkbox"
                        class="size-4.5 rounded-sm border-gray-300 text-sky-700 group-hover:hover:border-gray-900 group-hover:border-gray-400 cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:z-40 focus:ring-gray-700 hover:border-gray-700 aria-disabled:focus-visible:ring-gray-500 aria-disabled:cursor-not-allowed aria-disabled:border-gray-300 aria-disabled:bg-gray-400 aria-disabled:text-gray-500 aria-disabled:hover:border-gray-300"
                        :class="[
                            {
                                'aria-disabled:bg-gray-400': checkbox.status === 'Disabled-Selected',
                                'aria-disabled:bg-gray-200': checkbox.status === 'Disabled-Not-Selected',
                            },
                        ]"
                        @mousedown.stop
                        @click="handleClick"
                        @change="handleToggle"
                    />
                </template>

                <template #text>
                    {{ checkboxTooltip }}
                </template>
            </Tooltip>

            <div
                class="flex flex-row items-center gap-2 pr-1 w-full"
                :class="[isToggleInert ? 'cursor-not-allowed' : 'cursor-pointer']"
                @click="handleToggleExpanded"
            >
                <!-- Empty section: nothing to expand/collapse, so show the warning in place of the triangle. -->
                <Tooltip v-if="isEmpty" :is-teleported="true">
                    <template #trigger="tooltipProps">
                        <WarningIcon
                            class="size-4.5 shrink-0 focus-visible:outline-2 rounded-xs cursor-help focus-visible:outline-offset-1 focus-visible:z-40 focus-visible:outline-gray-700"
                            v-bind="tooltipProps"
                            :tabindex="0"
                        />
                    </template>

                    <template #text>
                        <div class="w-max">{{ controlExplanation }}</div>
                    </template>
                </Tooltip>

                <Tooltip v-if="!isEmpty" :is-teleported="true">
                    <template #trigger="tooltipProps">
                        <button
                            class="shrink-0 flex items-center justify-center focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:z-40 focus-visible:outline-gray-700 rounded-xs cursor-pointer aria-disabled:cursor-not-allowed aria-disabled:text-gray-500"
                            :aria-expanded="props.isExpanded"
                            :aria-disabled="isTriangleDisabled"
                            v-bind="tooltipProps"
                            type="button"
                            @click.stop="handleToggleExpanded"
                        >
                            <TriangleIcon
                                class="transition-transform rounded-xs size-4.5"
                                :class="{ 'rotate-90': props.isExpanded }"
                            />
                        </button>
                    </template>

                    <template #text>
                        <template v-if="controlExplanation">
                            {{ controlExplanation }}
                        </template>

                        <template v-else>
                            {{ props.isExpanded ? $t('collapse-timeline-section') : $t('expand-timeline-section') }}
                        </template>
                    </template>
                </Tooltip>

                <div v-if="checkbox.status === 'No-Selection'" class="shrink-0 flex items-center justify-center">
                    <Tooltip>
                        <template #trigger="tooltipProps">
                            <Component
                                :is="props.icon.component"
                                class="size-4.5 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:z-40 focus-visible:outline-gray-700 rounded-xs cursor-help"
                                :tabindex="0"
                                v-bind="tooltipProps"
                            />
                        </template>

                        <template #text>
                            {{ props.icon.title }}
                        </template>
                    </Tooltip>
                </div>

                <slot name="label" :label-id="labelId" />

                <div v-if="props.conflictsCount.total > 0" class="normal-case flex ml-auto">
                    <Tooltip v-if="props.conflictsCount.unresolved === 0">
                        <template #trigger="tooltipProps">
                            <div class="flex items-center normal-case">
                                <FlashOffIcon
                                    class="focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:z-40 focus-visible:outline-gray-700 rounded-xs tabular-nums size-4 cursor-help"
                                    v-bind="tooltipProps"
                                    tabindex="0"
                                />
                            </div>
                        </template>

                        <template #text>
                            <div class="w-max">
                                <i18n
                                    path="all-conflicts-resolved-tooltip"
                                    :args="{ count: props.conflictsCount.total }"
                                >
                                    <template #conflictCount>
                                        <span class="font-semibold">
                                            {{ props.conflictsCount.total }}
                                        </span>
                                    </template>
                                </i18n>
                            </div>
                        </template>
                    </Tooltip>

                    <Tooltip v-if="hasUnresolvedConflicts">
                        <template #trigger="tooltipProps">
                            <div
                                class="flex flex-row gap-0.5 text-xs font-semibold items-center focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:z-40 focus-visible:outline-gray-700 rounded-xs tabular-nums cursor-help ml-0.75"
                                :class="[isHovered ? 'text-white' : 'text-red-700']"
                                v-bind="tooltipProps"
                                tabindex="0"
                            >
                                <span>
                                    {{ props.conflictsCount.unresolved }}
                                </span>

                                <FlashIcon class="size-4" />
                            </div>
                        </template>

                        <template #text>
                            <div class="w-max">
                                <i18n
                                    path="unresolved-conflicts-tooltip"
                                    :args="{ count: props.conflictsCount.unresolved }"
                                >
                                    <template #unresolvedCount>
                                        <span class="font-semibold tabular-nums">
                                            {{ props.conflictsCount.unresolved }}
                                        </span>
                                    </template>

                                    <template #totalCount>
                                        <span class="tabular-nums">
                                            {{ props.conflictsCount.total }}
                                        </span>
                                    </template>
                                </i18n>
                            </div>
                        </template>
                    </Tooltip>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import FlashIcon from '@/components/Icon/FlashIcon.vue';
import FlashOffIcon from '@/components/Icon/FlashOffIcon.vue';
import TriangleIcon from '@/components/Icon/TriangleIcon.vue';
import WarningIcon from '@/components/Icon/WarningIcon.vue';
import { triggerHideTooltipKey } from '@/components/Tooltip/injectionKeys.ts';
import {
    type Component,
    computed,
    inject,
    onMounted,
    onUnmounted,
    provide,
    ref,
    useId,
    useTemplateRef,
    watchEffect,
} from 'vue';
import Tooltip from '../../Tooltip/Tooltip.vue';
import { useActiveStyleSync } from '../composables/useActiveStyleSync.ts';
import { useHeightSync } from '../composables/useHeightSync.ts';
import type { SectionExpandableState } from '../composables/useSectionRows.ts';
import { hoveredTimelinePartsKey, hoverTimelinePartKey } from '../injectionKeys.ts';
import { type CheckboxState, useLocationSelectionStore } from '../LocationSelection.store.ts';

type SectionFirstColumnCellProps = {
    /** Shared row identity: pairs this cell with its days row for hover and height sync. */
    rowId: string;
    sectionId: string;
    conflictsCount: {
        resolved: number;
        unresolved: number;
        total: number;
    };
    isExpanded: boolean;
    /**
     * The state of the expand/collapse control: `Enabled` (toggleable), `Disabled` (locked because the
     * section holds the selection source — triangle shown but disabled), or `Empty` (no locations — a
     * warning icon replaces the triangle). `Disabled`/`Empty` carry the tooltip explanation.
     */
    expandableState: SectionExpandableState;
    icon: {
        component: Component;
        title: string;
    };
};

const props = defineProps<SectionFirstColumnCellProps>();

const emit = defineEmits<{
    (e: 'toggle-section-expanded'): void;
}>();

const locationSelectionStore = useLocationSelectionStore();

// Registered in onMounted, not setup: switching views unmounts the old rows and mounts the new ones, and
// Vue runs the unmounted hooks first. Registering earlier than that would let the old row's unregister
// delete an entry the new row had just created. `checkbox` below covers the render-before-mount window.
onMounted(() => {
    locationSelectionStore.registerSection(props.sectionId);
});

onUnmounted(() => {
    locationSelectionStore.unregisterSection(props.sectionId);
});

const checkbox = computed<CheckboxState>(() => {
    // Not registered yet (first render, before onMounted) — no checkbox rather than a thrown guard.
    if (!locationSelectionStore.isSectionRegistered(props.sectionId)) {
        return { status: 'No-Selection' };
    }
    return locationSelectionStore.getSectionCheckbox(props.sectionId);
});

const checkboxTooltip = computed(() => {
    if (checkbox.value.status === 'No-Selection') {
        return '';
    }
    return checkbox.value.tooltip;
});

const labelId = useId();

const isDisabled = computed(
    () => checkbox.value.status === 'Disabled-Selected' || checkbox.value.status === 'Disabled-Not-Selected',
);

const isChecked = computed(() => checkbox.value.status === 'Selected' || checkbox.value.status === 'Disabled-Selected');

const isEmpty = computed(() => props.expandableState.type === 'Empty');
// The triangle is shown but locked when the section holds the selection source.
const isTriangleDisabled = computed(() => props.expandableState.type === 'Disabled');
// The expand/collapse affordance is inert unless enabled — in the disabled/empty states the row must
// not toggle or register hover/pressed styling.
const isToggleInert = computed(() => props.expandableState.type !== 'Enabled');
// Tooltip text for the disabled/empty states (the warning icon and the locked triangle both use it).
const controlExplanation = computed(() =>
    props.expandableState.type === 'Enabled' ? undefined : props.expandableState.explanation,
);

const hoverTimelinePart = inject(hoverTimelinePartKey);
if (!hoverTimelinePart) {
    throw new Error('SectionFirstColumnCell must be used within a Timeline component');
}

const hoveredTimelineParts = inject(hoveredTimelinePartsKey);
if (!hoveredTimelineParts) {
    throw new Error('SectionFirstColumnCell must be used within a Timeline component');
}

const isHovered = computed(() => hoveredTimelineParts.rowId.value === props.rowId);

const { heightInRem } = useHeightSync(props.rowId, 1.5);
const minHeightStyle = computed(() => {
    return {
        minHeight: `${heightInRem.value}rem`,
    };
});

const { isActive, setActive, setInactive } = useActiveStyleSync(props.rowId);

const hasUnresolvedConflicts = computed(() => props.conflictsCount.unresolved > 0);

const backgroundClass = computed(() => {
    if (isHovered.value) {
        if (hasUnresolvedConflicts.value) {
            return isActive.value ? 'bg-red-800' : 'bg-red-700';
        }

        return isActive.value ? 'bg-gray-800' : 'bg-gray-700';
    }

    if (hasUnresolvedConflicts.value) {
        return isActive.value ? 'bg-red-200' : 'bg-red-100';
    }

    return isActive.value ? 'bg-gray-300' : 'bg-gray-200';
});

const textColorClass = computed(() => {
    if (isEmpty.value) {
        return 'text-red-700';
    }

    if (isHovered.value) {
        if (hasUnresolvedConflicts.value) {
            return 'text-white';
        }

        return 'text-gray-50';
    }

    return 'text-gray-900';
});

const borderVerticalClasses = computed(() => {
    if (isHovered.value) {
        if (hasUnresolvedConflicts.value) {
            return 'section-first-column-cell-vertical section-first-column-cell-vertical-red-700 section-first-column-cell-vertical-z-20';
        }

        return 'section-first-column-cell-vertical section-first-column-cell-vertical-gray-600 section-first-column-cell-vertical-z-20';
    }

    if (hasUnresolvedConflicts.value) {
        return 'section-first-column-cell-vertical section-first-column-cell-vertical-red-200 section-first-column-cell-vertical-z-20';
    }

    return 'section-first-column-cell-vertical section-first-column-cell-vertical-gray-300 section-first-column-cell-vertical-z-10';
});

const borderRightClasses = computed(() => {
    if (isHovered.value) {
        if (hasUnresolvedConflicts.value) {
            return 'section-first-column-cell-right-2-max-height section-first-column-cell-right-red-900';
        }

        return 'section-first-column-cell-right-2-max-height section-first-column-cell-right-gray-950';
    }

    if (hasUnresolvedConflicts.value) {
        return 'section-first-column-cell-right-2-max-height section-first-column-cell-right-red-700';
    }
});

const checkboxRef = useTemplateRef('checkboxRef');
watchEffect(() => {
    if (checkboxRef.value) {
        checkboxRef.value.indeterminate = checkbox.value.status === 'Partially-Selected';
    }
});

// This is used to force close the Tooltip after clicking the button
const triggerHideTooltipKeyCounter = ref(0);
provide(triggerHideTooltipKey, triggerHideTooltipKeyCounter);

function triggerHideTooltip() {
    triggerHideTooltipKeyCounter.value = triggerHideTooltipKeyCounter.value + 1;
}

const handleToggleExpanded = () => {
    // Return early (without hiding the tooltip) so the disabled/empty explanation stays visible.
    if (isToggleInert.value) {
        return;
    }
    triggerHideTooltip();
    emit('toggle-section-expanded');
};

// A locked or empty section is not interactive, so it must not highlight itself. But the mouse IS over
// it now, so clear any previous row's hover (hover nothing) rather than leaving a stale highlight behind.
const handleRowMouseOver = () => {
    hoverTimelinePart(isToggleInert.value ? null : props.rowId, null, false);
};

const handleRowMouseDown = () => {
    if (isToggleInert.value) {
        return;
    }
    setActive();
};

function handleClick(event: MouseEvent) {
    triggerHideTooltip();

    event.stopPropagation();
    if (isDisabled.value) {
        event.preventDefault();
    }
}

function handleToggle() {
    if (isDisabled.value) {
        return;
    }
    locationSelectionStore.toggleSectionSelection(props.sectionId);
}
</script>

<style scoped>
.section-first-column-cell-vertical,
.section-first-column-cell-right {
    position: relative;
}

.section-first-column-cell-vertical::before {
    content: '';
    position: absolute;
    top: -1px;
    left: 0;
    width: 100%;
    height: 1px;
}

.section-first-column-cell-vertical::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 1px;
}

.section-first-column-cell-vertical-z-10::before,
.section-first-column-cell-vertical-z-10::after {
    z-index: 10;
}

.section-first-column-cell-vertical-z-20::before,
.section-first-column-cell-vertical-z-20::after {
    z-index: 30;
}

.section-first-column-cell-vertical-gray-300::before,
.section-first-column-cell-vertical-gray-300::after {
    background-color: var(--color-gray-300);
}

.section-first-column-cell-vertical-gray-600::before,
.section-first-column-cell-vertical-gray-600::after {
    background-color: var(--color-gray-600);
}

.section-first-column-cell-vertical-red-200::before,
.section-first-column-cell-vertical-red-200::after {
    background-color: var(--color-red-200);
}

.section-first-column-cell-vertical-red-700::before,
.section-first-column-cell-vertical-red-700::after {
    background-color: var(--color-red-700);
}

.section-first-column-cell-right-2-max-height::after {
    content: '';
    position: absolute;
    top: -1px;
    right: -2px;
    width: 2px;
    height: calc(100% + 2px);
}

.section-first-column-cell-right-gray-950::after {
    background-color: var(--color-gray-950);
}

.section-first-column-cell-right-red-700::after {
    background-color: var(--color-red-700);
}

.section-first-column-cell-right-red-900::after {
    background-color: var(--color-red-900);
}
</style>
